import type { Form } from 'factorial-form'
import mapValues from 'lodash/mapValues'
import { reaction } from 'mobx'

import asyncAction from 'modules/core/lib/asyncAction'

import { actions } from 'modules/automations/store/collections/actions'
import { Action } from 'modules/automations/store/models/Action'
import type { Automation } from 'modules/automations/store/models/Automation'
import type { ITriggerConditions } from 'modules/automations/store/models/Trigger'

import { Branch } from './Branch'
import { RequestReview } from './RequestReview'
import { SendCustomNotification } from './SendCustomNotification'
import { SendNotification } from './SendNotification'
import type { Storeable } from './Storeable'
import { TimeAction } from './TimeAction'

type ActionToForm = Record<string, Form>
type ActionToStore = Record<string, Storeable>

export class Store {
  #automation: Automation
  #stores: ActionToStore = {}
  disposer: () => void

  constructor(automation: Automation, conditions: ITriggerConditions[]) {
    this.#automation = automation
    this.disposer = reaction(
      () =>
        actions.filter({
          automations_automation_id: this.#automation.attributes.id,
        }),
      (actions) => {
        this.#stores = this._generateStores(actions, conditions)
      },
      {
        fireImmediately: true,
      }
    )
  }

  get forms(): ActionToForm {
    return mapValues(this.#stores, (store) => store.form)
  }

  get stores(): ActionToStore {
    return mapValues(this.#stores, (store) => store)
  }

  dispose() {
    this.disposer()
    Object.values(this.#stores).forEach((store) => store.dispose())
  }

  _generateStore(action: Action, conditions: ITriggerConditions[]): Storeable | null | undefined {
    switch (action.type) {
      case Action.Types.requestReview:
        return new RequestReview(action)

      case Action.Types.branch:
        return new Branch(action, conditions)

      case Action.Types.sendNotification:
        return new SendNotification(action)

      case Action.Types.sendCustomNotification:
        return new SendCustomNotification(action)

      case Action.Types.time:
        return new TimeAction(action)

      default:
        return null
    }
  }

  _generateStores(actions: Action[], conditions: ITriggerConditions[]): ActionToStore {
    return actions.reduce<ActionToStore>((memo, action) => {
      const store = this.#stores[action.attributes.id] || this._generateStore(action, conditions)

      if (!store) return memo

      memo[action.attributes.id] = store

      return memo
    }, {})
  }

  save() {
    return asyncAction(
      () => Promise.all(Object.values(this.#stores).map((store) => store.save())),
      { successFlash: false }
    )
  }
}
